import styles from './SidePanelFilePreview.module.css';
import React, { ReactNode, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { playerLink } from '@he-novation/config/paths/herawFrontUris';
import { Button } from '@he-novation/design-system/components/buttons/Button/Button';
import { Icon } from '@he-novation/design-system/components/graphics/Icon/Icon';
import { FrontFolderContentFile } from '@he-novation/front-shared/types/file.front-types';
import { isVideoType } from '@he-novation/utils/mimeType.utils';
import cn from 'classnames';
import { useAtomValue } from 'jotai';

import { workspaceNameAtom } from '$atoms/workspace-atoms';
import { FolderItemFigureContentImage } from '$components/DataLayout/DataLayoutFolder/FolderItemFigure/FolderItemFigure';
import { set } from '$redux/route/routeActions';

type SidePanelFilePreviewProps = Pick<
    FrontFolderContentFile,
    'name' | 'uuid' | 'version' | 'preview' | 'thumbnail' | 'isPlayable' | 'playerAssets'
> & {
    mimeType?: string | null;
    className?: string;
};

export function SidePanelFilePreview({
    name,
    uuid,
    version,
    mimeType,
    preview,
    thumbnail,
    playerAssets,
    isPlayable,
    className
}: SidePanelFilePreviewProps) {
    const dispatch = useDispatch();

    const workspaceName = useAtomValue(workspaceNameAtom);

    const videoRef = useRef<HTMLVideoElement>(null);
    const [isPlaying, setIsPlaying] = React.useState(false);
    const [isPausing, setIsPausing] = React.useState(false);

    let content: ReactNode;

    const previewUrl =
        isVideoType(mimeType) && playerAssets.length > 0
            ? playerAssets[playerAssets.length - 1].url
            : preview;

    useEffect(() => {
        setIsPausing(false);
        setIsPlaying(false);
    }, [previewUrl]);

    if (previewUrl) {
        content = (
            <>
                {!isPausing && (
                    <Icon
                        icon={'play'}
                        fill={'white'}
                        className={cn(styles.play, isPlaying && 'playing')}
                    />
                )}
                <Icon
                    icon="pause"
                    fill={'white'}
                    className={cn(styles.pause, isPausing && 'pausing')}
                />
                <Button
                    icon="fullscreen"
                    className={cn(styles.goToFile)}
                    onClick={() => {
                        dispatch(
                            set(
                                playerLink(workspaceName, uuid, version, {
                                    currentTime: videoRef.current?.currentTime
                                })
                            )
                        );
                    }}
                />
                <video
                    ref={videoRef}
                    src={previewUrl}
                    poster={thumbnail || undefined}
                    preload="none"
                    className={styles.video}
                    onEnded={() => {
                        setTimeout(() => {
                            if (videoRef.current) videoRef.current.currentTime = 0;
                            setIsPlaying(false);
                            setIsPausing(false);
                        }, 200);
                    }}
                    onClick={() => {
                        if (!videoRef.current) return;

                        if (videoRef.current.paused) {
                            videoRef.current.play();
                            setIsPlaying(true);
                        } else {
                            setIsPausing(true);
                            videoRef.current.pause();
                            setIsPlaying(false);
                            setTimeout(() => {
                                setIsPausing(false);
                            }, 200);
                        }
                    }}
                />
            </>
        );
    } else if (thumbnail) {
        content = <img src={thumbnail || undefined} alt="" className={styles.thumbnail} />;
    } else {
        content = (
            <FolderItemFigureContentImage
                name={name}
                mimeType={mimeType}
                isPlayable={isPlayable}
                className={styles.icons}
            />
        );
    }

    return <div className={cn(styles.preview, className)}>{content}</div>;
}
